import { useMemo } from "react";
import type { GridColDef } from "@mui/x-data-grid";
import Badge from "@components/Badge/Badge";
import type { CargoOperationDetail } from "@services/drybulkCargoOperationsApi";
import { useTranslation } from "react-i18next";
import type { DetailExtensions } from "../components/detailExtensions";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import ExecutionDialogs from "../execution/ExecutionDialogs";
import ExecutionRowItems from "../execution/ExecutionRowItems";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.sequences.grid`;
const L_EXECUTION = `${DRYBULK_CARGO_OPS_LANG_BASE}.sequences.execution`;
const ATB_HINT_STATUSES = ["PENDING_SHIPPER_READINESS", "SHIPPER_READY"];

export const useDetailExtensions = (
  operationId: string | undefined,
  data: CargoOperationDetail | undefined,
  isOperationsUser: boolean,
  inherited?: DetailExtensions,
): DetailExtensions => {
  const { t } = useTranslation();
  const status = data?.status ?? null;
  const showTransporter = Boolean(
    data?.sequences?.some((sequence) => "transporterCompanyName" in sequence),
  );
  const atbPending = !data?.atb && ATB_HINT_STATUSES.includes(status ?? "");
  return useMemo(() => {
    const columns: GridColDef[] = [
      { field: "actualQuantity", headerName: t(`${L}.actual`), flex: 0.8, minWidth: 70 },
      { field: "remainingQuantity", headerName: t(`${L}.remaining`), flex: 0.8, minWidth: 70 },
      ...(showTransporter
        ? [{ field: "transporter", headerName: t(`${L}.transporter`), flex: 1, minWidth: 100 }]
        : []),
    ];
    return {
      ...inherited,
      headerChips:
        atbPending || inherited?.headerChips ? (
          <>
            {!data?.atb && atbPending && (
              <Badge
                type="neutral"
                label={t(`${L_EXECUTION}.header.atb-missing`)}
                testId="drybulk-cargo-operation-atb-missing"
              />
            )}
            {inherited?.headerChips}
          </>
        ) : undefined,
      extraColumns: [...columns, ...(inherited?.extraColumns ?? [])],
      ...(isOperationsUser && operationId
        ? {
            rowActions: (sequence) => (
              <>
                <ExecutionRowItems sequence={sequence} operationStatus={status} />
                {inherited?.rowActions?.(sequence)}
              </>
            ),
            executionSection: (
              <>
                <ExecutionDialogs operationId={operationId} />
                {inherited?.executionSection}
              </>
            ),
          }
        : {}),
    } satisfies DetailExtensions;
  }, [t, data?.atb, atbPending, status, showTransporter, isOperationsUser, operationId, inherited]);
};
